import React, { useState, useEffect } from 'react';
import { Download, Bell, Check } from 'lucide-react';
import { Button, Stack, Text, Group, Card, Stepper } from '@mantine/core';
import { pwaService } from '../services/pwaService';

interface WelcomeSetupProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
}

export const WelcomeSetup: React.FC<WelcomeSetupProps> = ({ isOpen, onClose, onComplete }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isPWAInstalled, setIsPWAInstalled] = useState(false);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);


  useEffect(() => {
    if (isOpen) {
      checkPWAStatus();
    }
  }, [isOpen]);

  const checkPWAStatus = () => {
    setIsPWAInstalled(pwaService.isAppInstalled());
    setShowInstallPrompt(pwaService.isInstallPromptAvailable());
    
    // If PWA is already installed, skip to notifications
    if (pwaService.isAppInstalled()) {
      setActiveStep(2); // Skip welcome (0) and install (1) to notifications (2)
    }
  };

  const handleInstallPWA = async () => {
    setIsLoading(true);
    try {
      const success = await pwaService.installApp();
      if (success) {
        setIsPWAInstalled(true);
        setActiveStep(2); // Go to notifications step
      } else {
        // User cancelled installation
        setActiveStep(2); // Go to notifications step
      }
    } catch (error) {
      console.error('Failed to install PWA:', error);
      setActiveStep(2); // Go to notifications step
    } finally {
      setIsLoading(false);
    }
  };

  const handleEnableNotifications = async () => {
    setIsLoading(true);
    try {
      const success = await pwaService.subscribeToPushNotifications();
      if (success) {
        setActiveStep(3); // Go to complete step
      } else {
        setActiveStep(3); // Go to complete step
      }
    } catch (error) {
      console.error('Failed to enable notifications:', error);
      setActiveStep(3); // Go to complete step
    } finally {
      setIsLoading(false);
    }
  };

  const handleSkip = () => {
    if (activeStep === 1) { // install step
      setActiveStep(2); // Go to notifications
    } else if (activeStep === 2) { // notifications step
      setActiveStep(3); // Go to complete
    } else {
      onClose();
    }
  };

  const handleComplete = () => {
    onComplete();
    onClose();
  };

  const handleNeverShowAgain = () => {
    // Save preference to localStorage
    localStorage.setItem('welcome-setup-never-show', 'true');
    onComplete();
    onClose();
  };

  const nextStep = () => setActiveStep((current) => Math.min(current + 1, 3));

  const renderStepContent = () => {
    if (activeStep === 0) {
      return (
        <Stack gap="md" align="center" ta="center">
          <Text c="dimmed">
            Let's get you set up to receive notifications and stay organized across all your devices.
          </Text>
          <Group justify="center" mt="md">
            <Button onClick={nextStep} size="md">
              Get Started
            </Button>
            <Button variant="subtle" onClick={onClose} size="md">
              Skip Setup
            </Button>
          </Group>
          <Group justify="center" mt="sm">
            <Button 
              variant="outline" 
              color="gray" 
              size="sm" 
              onClick={handleNeverShowAgain}
              style={{ 
                borderColor: '#e0e0e0',
                color: '#666'
              }}
            >
              Never show this again
            </Button>
          </Group>
        </Stack>
      );
    }

    if (activeStep === 1) {
      return (
        <Stack gap="md" align="center" ta="center">
          <Text c="dimmed">
            Install SymphonyTodo as an app for the best experience. You'll get:
          </Text>
          <Stack gap="xs" align="flex-start" style={{ width: '100%' }}>
            <Group gap="sm">
              <Check size={16} color="green" />
              <Text size="sm">Faster loading and offline access</Text>
            </Group>
            <Group gap="sm">
              <Check size={16} color="green" />
              <Text size="sm">Push notifications</Text>
            </Group>
            <Group gap="sm">
              <Check size={16} color="green" />
              <Text size="sm">Native app-like experience</Text>
            </Group>
          </Stack>
          <Group justify="center" mt="md" style={{ width: '100%' }}>
            {showInstallPrompt ? (
              <Button 
                leftSection={<Download size={16} />}
                onClick={handleInstallPWA}
                loading={isLoading}
                size="md"
              >
                Install App
              </Button>
            ) : (
              <Card withBorder p="sm" style={{ width: '100%' }}>
                <Text size="sm" c="dimmed" ta="center">
                  To install: Look for the install button in your browser's address bar or menu
                </Text>
              </Card>
            )}
            <Button variant="subtle" onClick={handleSkip} size="md">
              Skip
            </Button>
          </Group>
        </Stack>
      );
    }

    if (activeStep === 2) {
      return (
        <Stack gap="md" align="center" ta="center">
          <Text c="dimmed">
            Stay updated with push notifications for:
          </Text>
          <Stack gap="xs" align="flex-start" style={{ width: '100%' }}>
            <Group gap="sm">
              <Check size={16} color="green" />
              <Text size="sm">Task assignments and updates</Text>
            </Group>
            <Group gap="sm">
              <Check size={16} color="green" />
              <Text size="sm">Due date reminders</Text>
            </Group>
            <Group gap="sm">
              <Check size={16} color="green" />
              <Text size="sm">Comments and mentions</Text>
            </Group>
          </Stack>
          <Group justify="center" mt="md" style={{ width: '100%' }}>
            <Button 
              leftSection={<Bell size={16} />}
              onClick={handleEnableNotifications}
              loading={isLoading}
              size="md"
            >
              Enable Notifications
            </Button>
            <Button variant="subtle" onClick={handleSkip} size="md">
              Maybe Later
            </Button>
          </Group>
        </Stack>
      );
    }

    if (activeStep === 3) {
      return (
        <Stack gap="md" align="center" ta="center">
          <Text c="dimmed">
            {isPWAInstalled && pwaService.isPushNotificationsEnabled()
              ? "You'll receive push notifications on this device. Install the app on your other devices to stay synced everywhere!"
              : isPWAInstalled
                ? "The app is installed! You can enable notifications later in Settings."
                : "You can install the app and enable notifications anytime in Settings."
            }
          </Text>
          <Button onClick={handleComplete} size="md" mt="md">
            Start Using SymphonyTodo
          </Button>
        </Stack>
      );
    }

    return null;
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000,
      padding: '20px'
    }}>
      <div style={{
        position: 'relative',
        backgroundColor: 'var(--app-surface, white)',
        borderRadius: '8px',
        padding: '32px',
        maxWidth: '600px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
        color: 'var(--app-text-primary, black)'
      }}>
        <div style={{
          position: 'absolute',
          top: '16px',
          right: '16px',
          cursor: 'pointer',
          fontSize: '24px',
          color: 'var(--app-text-secondary, #666)',
          zIndex: 1,
          width: '24px',
          height: '24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }} onClick={onClose}>
          ×
        </div>
        
        <Stack gap="lg">
          <Text size="xl" fw={600} ta="center">Welcome to SymphonyTodo!</Text>
          
          <div>
            {/* Mobile Layout */}
            <div style={{ 
              display: window.innerWidth <= 768 ? 'block' : 'none',
              textAlign: 'center'
            }}>
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '2rem' }}>
                <Stepper 
                  active={activeStep} 
                  onStepClick={setActiveStep} 
                  allowNextStepsSelect={false}
                  orientation="vertical"
                  size="sm"
                >
                  <Stepper.Step 
                    label="Welcome" 
                    description="Get started"
                    icon={<span style={{ fontSize: '1.2rem' }}>🎉</span>}
                  />
                  <Stepper.Step 
                    label="Install App" 
                    description="Install for best experience"
                    icon={<Download size={16} />}
                  />
                  <Stepper.Step 
                    label="Notifications" 
                    description="Stay updated"
                    icon={<Bell size={16} />}
                  />
                  <Stepper.Step 
                    label="Complete" 
                    description="You're all set!"
                    icon={<span style={{ fontSize: '1.2rem' }}>✅</span>}
                  />
                </Stepper>
              </div>
              
              {/* Mobile content */}
              {renderStepContent()}
            </div>

            {/* Desktop Layout */}
            <div style={{ 
              display: window.innerWidth > 768 ? 'flex' : 'none',
              gap: '2rem',
              minHeight: '300px'
            }}>
              <div style={{ flex: '0 0 200px' }}>
                <Stepper 
                  active={activeStep} 
                  onStepClick={setActiveStep} 
                  allowNextStepsSelect={false}
                  orientation="vertical"
                  size="sm"
                >
                  <Stepper.Step 
                    label="Welcome" 
                    description="Get started"
                    icon={<span style={{ fontSize: '1.2rem' }}>🎉</span>}
                  />
                  <Stepper.Step 
                    label="Install App" 
                    description="Install for best experience"
                    icon={<Download size={16} />}
                  />
                  <Stepper.Step 
                    label="Notifications" 
                    description="Stay updated"
                    icon={<Bell size={16} />}
                  />
                  <Stepper.Step 
                    label="Complete" 
                    description="You're all set!"
                    icon={<span style={{ fontSize: '1.2rem' }}>✅</span>}
                  />
                </Stepper>
              </div>
              
              <div style={{ 
                flex: 1, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center' 
              }}>
                {/* Desktop content */}
                {renderStepContent()}
              </div>
            </div>
          </div>
        </Stack>
      </div>
    </div>
  );
};

export default WelcomeSetup;